import { FC, useState } from 'react';

import operationTypes from 'constants/operationTypes';
import { useTranslate } from 'hooks/useTranslate';
import { formatNumber } from 'utils/formats';

import {
  SharesTransferExcelOperation,
  SharesTransferExcelRow,
  SharesTransferExcelValidationError,
} from '../types/SharesTransferExcel';
import { getSharesTransferExcelValidationMessages } from '../utils/getSharesTransferExcelValidationMessages';
import { useSharesTransferExcelModal } from '../context/SharesTransferExcelModalContext';

type OperationCardProps = {
  rowIndex: number;
  operation: SharesTransferExcelOperation;
  onSelectType: (operationId: string, operationType: string) => void;
  rowErrors: Set<number>;
};

const formatDate = (date: Date): string => {
  const hasTime =
    date.getHours() !== 0 ||
    date.getMinutes() !== 0 ||
    date.getSeconds() !== 0;

  if (hasTime) {
    return date.toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  return date.toLocaleDateString('es-ES', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

const getOperationTypeLabel = (type: string, t: (key: string) => string) => {
  const keyMap: Record<string, string> = {
    [operationTypes.SELL_PARTICIPATION]: 'Sale',
    [operationTypes.HERITAGE]: 'Heritage',
    [operationTypes.DONATION]: 'Donation',
    [operationTypes.ENDORSEMENT]: 'Endorsement',
    [operationTypes.TRANSMISSION]: 'Transmission',
  };

  return t(keyMap[type] || type);
};

const SharesTransferExcelOperationCard: FC<OperationCardProps> = ({
  rowIndex,
  operation,
  onSelectType,
  rowErrors,
}) => {
  const { t } = useTranslate();
  const [isExpanded, setIsExpanded] = useState(false);

  const sellerCount = operation.sellers.length;
  const buyerCount = operation.buyers.length;
  const hasErrors = !operation.isValid;

  const renderMovementRow = (row: SharesTransferExcelRow) => {
    const hasRowError = rowErrors.has(row.rowIndex);
    const signPrefix = row.isSeller ? '-' : '+';

    return (
      <tr
        key={`${row.rowIndex}-${row.cif}-${row.shareFrom}`}
        className={hasRowError ? 'nk-tb-item table-danger' : 'nk-tb-item'}
      >
        <td className="nk-tb-col text-left">
          {hasRowError && (
            <em className="icon ni ni-alert-circle text-danger mr-1" />
          )}
          <span
            className={
              row.isExistingPartner
                ? 'badge badge-dim badge-info'
                : 'badge badge-dim badge-success'
            }
          >
            {row.isExistingPartner
              ? t('ConstitutionPartnerStatusExisting')
              : t('ConstitutionPartnerStatusNew')}
          </span>
        </td>
        <td className="nk-tb-col text-left">{row.name}</td>
        <td className="nk-tb-col text-left">{row.cif}</td>
        <td className="nk-tb-col text-right">
          {signPrefix}
          {formatNumber(Math.abs(row.totalShares))}
        </td>
        <td className="nk-tb-col text-right">{formatNumber(row.shareFrom)}</td>
        <td className="nk-tb-col text-right">{formatNumber(row.shareTo)}</td>
        <td className="nk-tb-col text-right">
          {row.sharePrice != null ? formatNumber(row.sharePrice) : '-'}
        </td>
      </tr>
    );
  };

  return (
    <div
      className="shares-transfer-excel-modal__operation-card"
      data-testid={`shares-transfer-op-${operation.operationId}`}
    >
      <div
        className="shares-transfer-excel-modal__operation-header d-flex justify-content-between align-items-center"
        onClick={() => setIsExpanded(!isExpanded)}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            setIsExpanded(!isExpanded);
          }
        }}
      >
        <div className="shares-transfer-excel-modal__operation-header-main d-flex align-items-center flex-grow-1">
          <span className="shares-transfer-excel-modal__operation-index text-soft small">
            {rowIndex + 1}
          </span>
          <span
            className="shares-transfer-excel-modal__operation-id"
            title={operation.operationId}
          >
            {operation.operationId}
          </span>
          <span className="text-soft">{formatDate(operation.date)}</span>
          {operation.needsTypeSelection ? (
            <select
              className="form-control form-control-sm d-inline-block ml-2"
              style={{ width: 'auto' }}
              value={operation.selectedOperationType || ''}
              onClick={(event) => event.stopPropagation()}
              onChange={(event) =>
                onSelectType(operation.operationId, event.target.value)
              }
              data-testid={`shares-transfer-type-select-${operation.operationId}`}
            >
              <option value="">{t('SharesTransferExcelSelectType')}</option>
              {operation.operationTypes.map((type) => (
                <option key={type} value={type}>
                  {getOperationTypeLabel(type, t)}
                </option>
              ))}
            </select>
          ) : (
            <span className="badge badge-dim badge-primary ml-2">
              {getOperationTypeLabel(
                operation.selectedOperationType ||
                  operation.operationTypes[0] ||
                  '',
                t
              )}
            </span>
          )}
        </div>

        <div className="d-flex align-items-center">
          <span className="text-soft small mr-3">
            {t('SharesTransferExcelSellersBuyersSummary', {
              sellers: sellerCount,
              buyers: buyerCount,
              movements: operation.rows.length,
            })}
          </span>
          <span
            className={`badge ${hasErrors ? 'badge-danger' : 'badge-success'}`}
          >
            {hasErrors
              ? t('SharesTransferExcelOperationHasErrors')
              : t('SharesTransferExcelOperationValid')}
          </span>
          <em
            className={`icon ni ni-chevron-${isExpanded ? 'up' : 'down'} ml-2`}
          />
        </div>
      </div>

      {isExpanded && (
        <div className="shares-transfer-excel-modal__operation-body">
          <div className="table-responsive shares-transfer-excel-modal__table-scroll">
            <table className="nk-tb-list nk-tb-ulist">
              <thead>
                <tr className="nk-tb-item nk-tb-head tb-tnx-head fs-11px">
                  <th className="nk-tb-col bg-lighter">{t('Status')}</th>
                  <th className="nk-tb-col bg-lighter">{t('PartnerName')}</th>
                  <th className="nk-tb-col bg-lighter">{t('Cif')}</th>
                  <th className="nk-tb-col bg-lighter">{t('Shares')}</th>
                  <th className="nk-tb-col bg-lighter">{t('OfTheNumber')}</th>
                  <th className="nk-tb-col bg-lighter">{t('ToTheNumber')}</th>
                  <th className="nk-tb-col bg-lighter">{t('Price')}</th>
                </tr>
              </thead>
              <tbody>{operation.rows.map(renderMovementRow)}</tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const SharesTransferExcelPreview: FC = () => {
  const { t } = useTranslate();
  const { data, actions } = useSharesTransferExcelModal();

  const preview = data.excelPreview;

  if (!preview) {
    return null;
  }

  const hasErrors = preview.totalErrors > 0;
  const errorMessages = getSharesTransferExcelValidationMessages(
    preview.errors,
    t
  );
  const rowErrorIndexes = new Set(
    preview.errors
      .map((error: SharesTransferExcelValidationError) => error.rowIndex)
      .filter((rowIndex): rowIndex is number => Boolean(rowIndex))
  );

  const renderStatCard = (
    iconClass: string,
    value: string,
    label: string,
    toneClass: string
  ) => (
    <div className="col-sm-6 col-xl-3">
      <div className="card card-bordered shares-transfer-excel-modal__stat-card h-100">
        <div className="card-inner py-3">
          <div className="d-flex align-items-center">
            <em className={`icon ${iconClass} ${toneClass} mr-2`} />
            <div>
              <div
                className={`shares-transfer-excel-modal__stat-value font-weight-bold ${toneClass}`}
              >
                {value}
              </div>
              <span className="text-soft small">{label}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (!preview.operations.length) {
    return (
      <div className="text-center py-5">
        <p className="text-soft mb-3">{t('SharesTransferExcelNoOperations')}</p>
        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={actions.handleBackToUpload}
        >
          {t('BackToUpload')}
        </button>
      </div>
    );
  }

  return (
    <div className="shares-transfer-excel-modal__preview">
      {hasErrors ? (
        <div
          className="alert alert-danger alert-icon mb-4"
          data-testid="shares-transfer-preview-error-banner"
        >
          <em className="icon ni ni-alert-circle" />
          <strong>
            {t('SharesTransferExcelPreviewProblems', {
              count: preview.totalErrors,
            })}
          </strong>
          <ul className="shares-transfer-excel-modal__error-list mt-2">
            {errorMessages.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        </div>
      ) : (
        <div
          className="alert alert-success alert-icon mb-4"
          data-testid="shares-transfer-preview-success-banner"
        >
          <em className="icon ni ni-check-circle" />
          <span>{t('SharesTransferExcelPreviewReadyToExecute')}</span>
        </div>
      )}

      <div className="row g-gs mb-3">
        {renderStatCard(
          'ni ni-layers',
          formatNumber(preview.totalOperations),
          t('SharesTransferExcelOperationsCount'),
          'text-primary'
        )}
        {renderStatCard(
          'ni ni-list',
          formatNumber(preview.totalMovements),
          t('SharesTransferExcelMovementsCount'),
          'text-info'
        )}
        {renderStatCard(
          'ni ni-alert-circle',
          formatNumber(preview.totalErrors),
          t('SharesTransferExcelErrorsCount'),
          preview.totalErrors > 0 ? 'text-danger' : 'text-success'
        )}
        {renderStatCard(
          'ni ni-user-add',
          formatNumber(preview.totalNewPartners),
          t('SharesTransferExcelNewPartners'),
          'text-success'
        )}
      </div>

      {data.holdingClassName && (
        <p className="text-soft small mb-4">
          {t('ClassOfParticipations')}: <strong>{data.holdingClassName}</strong>
        </p>
      )}

      {data.isExecuting && (
        <div className="mb-4 shares-transfer-excel-modal__progress">
          <p className="mb-1">
            {t('SharesTransferExcelProgress', {
              completed: data.executeProgress.completed,
              total: data.executeProgress.total,
            })}
          </p>
          <div className="progress progress-lg">
            <div
              className="progress-bar"
              style={{
                width: `${
                  data.executeProgress.total
                    ? (data.executeProgress.completed /
                        data.executeProgress.total) *
                      100
                    : 0
                }%`,
              }}
            />
          </div>
        </div>
      )}

      <div className="shares-transfer-excel-modal__operations-list">
        {preview.operations.map((operation, index) => (
          <SharesTransferExcelOperationCard
            key={operation.operationId}
            rowIndex={index}
            operation={operation}
            onSelectType={actions.handleSelectOperationType}
            rowErrors={rowErrorIndexes}
          />
        ))}
      </div>
    </div>
  );
};

export default SharesTransferExcelPreview;
